import config from 'config';
import { cseHeaders } from 'commons-ui/src/context/ajax/useAjaxProvider';

const getHeaders = () => ({
	'Content-Type': 'application/json',
	Authorization: config.ADMIN_BASIC_AUTH,
	...cseHeaders()
});

const BASE = `${config.ADMIN_API_URL}/aiChatGateway`;

export const aiCompanionApi = {
	async deleteUserProfile(userId: string): Promise<{ success: boolean; data?: { message: string }; error?: string }> {
		const response = await fetch(`${BASE}/deleteUserProfile`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ userId })
		});
		return response.json();
	},

	async updateTalktime(
		userId: number,
		talktimeMinutes: number,
		subscriptionMinutes: number
	): Promise<{ success: boolean; data?: { message: string }; error?: string }> {
		const response = await fetch(`${BASE}/updateTalktime`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ userId, talktimeMinutes, subscriptionMinutes })
		});
		return response.json();
	},

	async blockUser(userId: string): Promise<{ success: boolean; data?: { message: string }; error?: string }> {
		const response = await fetch(`${BASE}/blockUser`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ userId })
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async bulkBlockUsers(userIds: string[]): Promise<{
		success: boolean;
		data?: { message: string; results?: { userId: string; success: boolean; error?: string }[] };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/bulkBlockUsers`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ userIds })
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async unblockUser(userId: string): Promise<{ success: boolean; data?: { message: string }; error?: string }> {
		const response = await fetch(`${BASE}/unblockUser`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ userId })
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async bulkUnblockUsers(userIds: string[]): Promise<{
		success: boolean;
		data?: { message: string; results?: { userId: string; success: boolean; error?: string }[] };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/bulkUnblockUsers`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({ userIds })
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async getViolatedMessages(req: { userId: string; botId: string }): Promise<{
		success: boolean;
		data?: {
			violations: Array<{
				messageId: string;
				category: string;
				severity?: string | null;
				timestamp: number;
				shouldCount?: boolean;
				groupId: string;
			}>;
			groups: Array<{
				groupId: string;
				category: string;
				severity?: string | null;
				timestamp: number;
				shouldCount?: boolean;
				messageIds: string[];
			}>;
		};
		error?: string;
	}> {
		const response = await fetch(`${BASE}/getViolatedMessages`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async getVoicePresignedUrl(req: { key: string; userId: string }): Promise<{
		success: boolean;
		data?: { downloadUrl: string; key: string; expiresIn: number };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/getVoicePresignedUrl`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async getChatHistory(req: { userId: string; botId: string; beforeSeq?: number; limit?: number }): Promise<{
		success: boolean;
		data?: {
			messages: Array<{
				id?: string;
				role?: string;
				content?: string;
				messageType?: string;
				s3Url?: string;
				seq?: number;
				ts?: number;
				final?: boolean;
				[k: string]: unknown;
			}>;
			nextCursor?: number;
			hasMore?: boolean;
		};
		error?: string;
	}> {
		const response = await fetch(`${BASE}/getChatHistory`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async listGuardrailActions(req: {
		queryType: 'TIMESTAMP_RANGE' | 'LAST_N_DAYS' | 'BY_USER_ID';
		params: Record<string, unknown>;
		page: number;
		pageSize: number;
	}): Promise<{
		success: boolean;
		data?: {
			items: Array<Record<string, unknown>>;
			total: number;
			page: number;
			pageSize: number;
		};
		error?: string;
	}> {
		const response = await fetch(`${BASE}/listGuardrailActions`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async getReviewQueue(req: {
		startTs?: number;
		endTs?: number;
		days?: number;
		category?: string;
		severity?: 'LOW' | 'MED' | 'HIGH' | 'CRIT';
		hasAction?: boolean;
		page?: number;
		pageSize?: number;
	}): Promise<{
		success: boolean;
		data?: {
			items: Array<{
				userId: string;
				botId: string;
				violationCount: number;
				pendingViolationCount?: number;
				actionCount: number;
				topCategory?: string;
				categories?: string[];
				severityCounts?: Record<string, number>;
				nullSeverityCount?: number;
				lastActionType?: string | null;
				hasBlock?: boolean;
				hasFlaggedForReview?: boolean;
				lastTimestamp?: number;
			}>;
			total: number;
			page: number;
			pageSize: number;
		};
		error?: string;
	}> {
		const response = await fetch(`${BASE}/getReviewQueue`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async getReviewCounters(req: { startTs?: number; endTs?: number; days?: number }): Promise<{
		success: boolean;
		data?: { pending: number; doneToday: number };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/getReviewCounters`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async submitViolationVerdict(req: {
		userId: string;
		botId: string;
		reviewerEmail: string;
		groupId: string;
		messageIds: string[];
		systemCategory: string;
		systemSeverity?: string | null;
		reviewerCorrectFlagged: 'yes' | 'no' | 'borderline';
		reviewerSeverity: 'LOW' | 'MED' | 'HIGH' | 'CRIT' | 'NOT_VIOLATION';
		reviewerCategory?: string | null;
		reviewerNotes?: string | null;
	}): Promise<{
		success: boolean;
		data?: { success: boolean; verdict: Record<string, unknown> };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/submitViolationVerdict`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async getGuardrailCategories(): Promise<{
		success: boolean;
		data?: { categories: string[] };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/getGuardrailCategories`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify({})
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	},

	async submitEnforcementVerdict(req: {
		userId: string;
		botId: string;
		actionId: string;
		actionType?: string | null;
		actionCategory?: string | null;
		actionTimestamp?: number | null;
		reviewerEmail: string;
		reviewedAt?: number;
		reviewerActionAppropriate?: 'appropriate' | 'too_lenient' | 'too_harsh' | null;
		reviewerBotResponseAppropriate?: 'yes' | 'no' | null;
		reviewerBlockJustified?: 'yes' | 'no' | 'escalate' | null;
		reviewerNotes?: string | null;
	}): Promise<{
		success: boolean;
		data?: { success: boolean; verdict: Record<string, unknown> };
		error?: string;
	}> {
		const response = await fetch(`${BASE}/submitEnforcementVerdict`, {
			method: 'POST',
			headers: getHeaders(),
			body: JSON.stringify(req)
		});
		const data = await response.json();
		if (!response.ok) {
			return { success: false, error: data?.error || data?.message || `Request failed with status ${response.status}` };
		}
		return data;
	}
};
